import React, { useCallback, useMemo, useState } from 'react';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { BookmarkIconSvg } from 'apps/sigma2-ui/src/components/svg/BookmarkIcon';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';
import { SavedAnalysisOutputSchema } from 'libs/end-to-end/src/saved-analysis/SavedAnalysisDefinitions';
import { CircularProgress } from '@mui/material';
import { useSavedAnalysis } from 'apps/sigma2-ui/src/filters-v2/lib/useSavedAnalysis';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { CardCheckbox } from 'apps/sigma2-ui/src/components/ui/CardCheckbox';
import { useIsUserAnalysisEnabled } from 'apps/sigma2-ui/src/components/ui/useUserAnalysis';
import { isEntitledToReports } from 'libs/end-to-end/src/group-entitlements';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';

export const SavedAnalysisCard = ({
  savedAnalysis,
  isChecked,
  onCheck,
}: {
  savedAnalysis: SavedAnalysisOutputSchema;
  isChecked: boolean;
  onCheck: () => void;
}) => {
  const { myObservation } = useObservation();
  const isUserAnalysisEnabled = useIsUserAnalysisEnabled();
  const language = useLanguage();

  const hasCheckbox = useMemo(
    () =>
      myObservation?.showDownloadMultiAnalysisButton &&
      isUserAnalysisEnabled &&
      isEntitledToReports({
        features: myObservation?.features ?? [],
      }),
    [
      isUserAnalysisEnabled,
      myObservation?.features,
      myObservation?.showDownloadMultiAnalysisButton,
    ],
  );

  const { customVizRequest, savedAnalysisId } = savedAnalysis;
  const cardContent = useCallback(
    () => (
      <>
        <Spacing
          direction={'horizontal'}
          size={'md'}
          className={'p-4 border-b-1 border-solid border-gray-200 items-start'}
        >
          <div className="text-gray-900 font-bold text-sm px-0.5 flex-1">
            <span>
              {convertTranslatedContentToReactNode(
                language.savedAnalysisCardTitle({
                  myObservation,
                  request: customVizRequest,
                }),
              )}
            </span>
          </div>
          {hasCheckbox && (
            <CardCheckbox checked={isChecked} onCheck={onCheck} />
          )}
        </Spacing>

        <div className="p-4 flex-grow">
          {convertTranslatedContentToReactNode(
            language.savedAnalysisCardBody({
              myObservation,
              request: customVizRequest,
            }),
          )}
        </div>
      </>
    ),
    [
      customVizRequest,
      hasCheckbox,
      isChecked,
      myObservation,
      onCheck,
      language,
    ],
  );

  const cardLinkBehavior: TouchTargetBehavior = useMemo(
    () => ({ type: 'href', href: savedAnalysis.shareLink }),
    [savedAnalysis.shareLink],
  );

  const [isLoading, setIsLoading] = useState(false);

  const { deleteAnalysis } = useSavedAnalysis();

  const unsaveBehavior: TouchTargetBehavior = useMemo(
    () => ({
      type: 'onClick',
      onClick: async () => {
        setIsLoading(true);
        await deleteAnalysis({ savedAnalysisId });
        setIsLoading(false);
      },
    }),
    [deleteAnalysis, savedAnalysisId],
  );

  const renderUnsaveContent = useCallback(
    ({
      hovered,
    }: {
      hovered: boolean;
      disabled: boolean;
      focused: boolean;
      active: boolean;
    }) => (
      <div
        className={cn('fill-current flex gap-x-2', {
          'text-gray-500': !hovered,
          'text-royal-800': hovered,
        })}
      >
        {isLoading ? (
          <CircularProgress
            size={16}
            className={cn({
              'text-royal-800': hovered,
              'text-gray-600': !hovered,
            })}
          />
        ) : (
          <BookmarkIconSvg />
        )}
        <span
          className={cn({
            'text-royal-800': hovered,
            'text-gray-600': !hovered,
          })}
        >
          Unsave
        </span>
      </div>
    ),
    [isLoading],
  );

  return (
    <Card
      paddingClassName="p-0"
      className="h-full flex flex-col justify-between"
      aria-label="saved-analysis-card"
    >
      <TouchTarget content={cardContent} behavior={cardLinkBehavior} />

      <div className="px-4 py-1.5 pb-3 flex justify-end">
        <TouchTarget
          className="flex items-center font-bold text-xs"
          content={renderUnsaveContent}
          behavior={unsaveBehavior}
        />
      </div>
    </Card>
  );
};
